import type { TFunction } from "i18next";
import {
	Activity,
	Award,
	Bell,
	Bookmark,
	Check,
	ChevronRight,
	Feather,
	FileText,
	type LucideIcon,
	MoreVertical,
	Thermometer,
	Upload,
} from "lucide-react";
import { useCallback } from "react";
import { toast } from "sonner";
import type {
	GetReadyRequiredDocument,
	GetReadyTaskProgressMeta,
	GetReadyTaskRenderModel,
} from "../../domain/getReadyTasks/types";
import { useAnalytics } from "../../lib/analytics";
import styles from "./TaskCard.module.css";

interface TaskCardProps {
	task: GetReadyTaskRenderModel;
	t: TFunction;
	/** Controls which card body is rendered. Defaults to "expanded". */
	view?: "collapsed" | "expanded";
	/** Called when the chevron/full-card navigate action fires. No-op stub in S02; wired in S03. */
	onNavigate?: () => void;
	/** Called when the Ask Eve pill fires. If absent, the pill is hidden. */
	onAskEve?: () => void;
	/** Called when the overflow (more options) button fires. If absent, the button is non-interactive. */
	onMoreOptions?: () => void;
}

const ACTION_ICON: Record<string, LucideIcon> = {
	fill: Feather,
	upload: Upload,
	book: Bookmark,
	"review-sign": Award,
	test: Thermometer,
	notify: Bell,
	monitor: Activity,
};

function getStatusLabel(
	status: GetReadyTaskRenderModel["status"],
	t: TFunction,
) {
	if (status === "completed") return t("task.status.completed");
	if (status === "in_progress") return t("task.status.inProgress");
	return t("task.status.pending");
}

function StatusIndicator({
	status,
	label,
}: {
	status: GetReadyTaskRenderModel["status"];
	label: string;
}) {
	if (status === "completed") {
		return (
			<span
				className={`${styles.statusIndicator} ${styles.statusCompleted}`}
				role="img"
				aria-label={label}
			>
				<Check className={styles.statusCheckIcon} aria-hidden="true" />
			</span>
		);
	}
	if (status === "in_progress") {
		return (
			<span
				className={`${styles.statusIndicator} ${styles.statusInProgress}`}
				role="img"
				aria-label={label}
			/>
		);
	}
	return (
		<span
			className={`${styles.statusIndicator} ${styles.statusPending}`}
			role="img"
			aria-label={label}
		/>
	);
}

function ProgressBlock({
	progress,
	label,
}: {
	progress: GetReadyTaskProgressMeta;
	label: string;
}) {
	return (
		<div className={styles.progressBlock}>
			<div className={styles.progressHeader}>
				<span className={styles.progressLabel}>{label}</span>
				<span className={styles.progressPercent}>{progress.percent}%</span>
			</div>
			<div className={styles.progressTrack}>
				<div
					className={styles.progressFill}
					style={
						{ "--progress-pct": `${progress.percent}%` } as React.CSSProperties
					}
				/>
			</div>
		</div>
	);
}

function RequiredDocItem({ doc }: { doc: GetReadyRequiredDocument }) {
	const isDone = doc.status === "uploaded" || doc.status === "completed";
	return (
		<div
			className={`${styles.docItem} ${isDone ? styles.docItemDone : styles.docItemPending}`}
		>
			<div className={styles.docIconRow}>
				<FileText
					className={styles.docIcon}
					strokeWidth={1.5}
					aria-hidden="true"
				/>
				{isDone && <Check className={styles.docCheckIcon} aria-hidden="true" />}
			</div>
			<span className={styles.docLabel}>{doc.label ?? doc.key}</span>
		</div>
	);
}

function RequiredDocsBlock({
	docs,
	t,
}: {
	docs: GetReadyRequiredDocument[];
	t: TFunction;
}) {
	return (
		<div className={styles.docsBlock}>
			<span className={styles.docsTitle}>{t("task.docs.title")}</span>
			<div className={styles.docsList}>
				{docs.map((doc) => (
					<RequiredDocItem key={doc.key} doc={doc} />
				))}
			</div>
		</div>
	);
}

/**
 * Get Ready task card.
 *
 * Supports two view modes:
 * - "collapsed": icon + title + subtext + status + chevron (list-style)
 * - "expanded": collapsed body plus progress/docs block and options row footer
 *
 * Category colors flow from --color-task-* tokens via the data-category attribute.
 */
export function TaskCard({
	task,
	t,
	view = "expanded",
	onNavigate,
	onAskEve,
	onMoreOptions,
}: TaskCardProps) {
	const { logEvent } = useAnalytics();
	const ActionIcon = ACTION_ICON[task.iconName] ?? Feather;
	const statusLabel = getStatusLabel(task.status, t);

	const handleCardPress = useCallback(() => {
		try {
			logEvent("get_ready_task_pressed", undefined, { taskKey: task.taskKey });
		} catch {
			// Analytics failures must not block the task interaction.
		}

		if (onNavigate) {
			onNavigate();
			return;
		}

		try {
			toast(t("task.cta.comingSoon"));
		} catch {
			// Toast failures must not block the button interaction.
		}
	}, [logEvent, task.taskKey, t, onNavigate]);

	const handleAskEve = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			try {
				logEvent("get_ready_ask_eve_pressed", undefined, {
					taskKey: task.taskKey,
				});
			} catch {
				// non-blocking
			}
			onAskEve?.();
		},
		[onAskEve, logEvent, task.taskKey],
	);

	// Determine expanded body variant
	const expandedVariant: "progress" | "docs" | "none" =
		view === "expanded"
			? task.requiredDocuments?.length
				? "docs"
				: task.progress
					? "progress"
					: "none"
			: "none";

	const cardDataProps = {
		"data-task-key": task.taskKey,
		"data-category": task.category ?? "default",
		"data-view": view,
		"data-kind": task.kind,
		className: styles.card,
	};

	const sharedBody = (
		<>
			{task.deadlineText ? (
				<div className={styles.deadlineRow}>
					<span className={styles.deadlineText}>{task.deadlineText}</span>
				</div>
			) : null}

			<div className={styles.bodyRow}>
				<ActionIcon
					className={styles.taskIcon}
					strokeWidth={1.5}
					aria-hidden="true"
				/>

				<div className={styles.content}>
					<span className={styles.title}>{task.title}</span>
					{task.subtext ? (
						<span className={styles.subtext}>{task.subtext}</span>
					) : null}
				</div>

				{view === "collapsed" ? (
					<>
						<StatusIndicator status={task.status} label={statusLabel} />
						<ChevronRight className={styles.chevronIcon} aria-hidden="true" />
					</>
				) : null}
			</div>
		</>
	);

	// Collapsed mode: semantically a button (no nested interactive elements).
	if (view === "collapsed") {
		return (
			<button
				type="button"
				{...cardDataProps}
				onClick={handleCardPress}
				aria-label={t("task.action.open", { title: task.title })}
			>
				{sharedBody}
			</button>
		);
	}

	// Expanded mode: plain div — all interactions handled by inner buttons
	// (more options, ask eve, chevron). No outer button avoids nested-button
	// invalid HTML.
	return (
		<div {...cardDataProps}>
			{sharedBody}

			{expandedVariant === "progress" && task.progress ? (
				<ProgressBlock
					progress={task.progress}
					label={t("task.progress.label", {
						current: task.progress.current,
						total: task.progress.total,
						unit: task.progress.unit,
					})}
				/>
			) : null}

			{expandedVariant === "docs" && task.requiredDocuments ? (
				<RequiredDocsBlock docs={task.requiredDocuments} t={t} />
			) : null}

			<div className={styles.optionsRow}>
				<div className={styles.optionsLeft}>
					<button
						type="button"
						className={styles.optionButton}
						aria-label={t("task.actions.moreOptions")}
						onClick={
							onMoreOptions
								? (e) => {
										e.stopPropagation();
										onMoreOptions();
									}
								: undefined
						}
						disabled={!onMoreOptions}
					>
						<MoreVertical className={styles.optionIcon} aria-hidden="true" />
					</button>

					{onAskEve ? (
						<button
							type="button"
							className={`${styles.optionButton} ${styles.askEvePill}`}
							onClick={handleAskEve}
						>
							<img
								src="/images/ask-eve-icon.png"
								alt=""
								className={styles.eveIcon}
							/>
							<span>{t("task.actions.askEve")}</span>
						</button>
					) : null}
				</div>

				<button
					type="button"
					className={`${styles.optionButton} ${styles.chevronPill}`}
					aria-label={t("task.actions.navigate", { title: task.title })}
					onClick={handleCardPress}
				>
					<ChevronRight className={styles.optionIcon} aria-hidden="true" />
				</button>
			</div>
		</div>
	);
}
